// ActivityFiveWordPuzzle.jsx — Chest activity (Phase 3: real logic).
// Matches escape_peace-old/js/wordpuzzle.js exactly: a fresh 10x10 grid is
// generated each time the activity opens (words placed horizontally,
// vertically, or diagonally, same as the source script), selection is a
// mouse/touch drag restricted to rightward/downward/down-right-diagonal
// paths, only the 4 positive words score (checked forward and reversed),
// each unique word scores once, and Submit appears once >= 1 word is found
// (room.php's confirmed minimum, shown in the modal subtitle).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ActivityPopup from '../overlays/ActivityPopup';
import { CHEST_TITLE, CHEST_SUBTITLE } from '../../../games/escape-to-inner-peace/data/gameText';
import {
  generateWordSearchGrid,
  cellsAlongPath,
  POSITIVE_WORDS,
} from '../../../games/escape-to-inner-peace/utils/wordSearchGrid';
import '../../../games/escape-to-inner-peace/styles/theme.css';
import './ActivityFiveWordPuzzle.css';

const key = (row, col) => `${row},${col}`;

function ActivityFiveWordPuzzle({ open, onComplete }) {
  const [grid] = useState(() => generateWordSearchGrid());
  const [selectedCells, setSelectedCells] = useState([]);
  const [foundCellKeys, setFoundCellKeys] = useState(() => new Set());
  const [foundWords, setFoundWords] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const draggingRef = useRef(false);
  const startCellRef = useRef(null);
  const foundWordsRef = useRef(new Set());
  // ESC-QA-11 — mirrors `selectedCells` state so `finishSelection` (see
  // below) can read the current selection from a stable callback without
  // depending on the state value itself.
  const selectedCellsRef = useRef([]);

  const selectedKeys = useMemo(() => new Set(selectedCells.map((c) => key(c.row, c.col))), [selectedCells]);

  const beginSelection = (row, col) => {
    if (submitted) return;
    draggingRef.current = true;
    startCellRef.current = { row, col };
    selectedCellsRef.current = [{ row, col }];
    setSelectedCells([{ row, col }]);
  };

  const extendSelection = (row, col) => {
    if (!draggingRef.current || !startCellRef.current) return;
    const path = cellsAlongPath(startCellRef.current.row, startCellRef.current.col, row, col);
    selectedCellsRef.current = path;
    setSelectedCells(path);
  };

  // ESC-QA-11 — a native `mouseup` fires on whatever element is under the
  // pointer at release and bubbles through *that* element's own ancestors,
  // not the drag's original `mousedown` target. Releasing outside
  // `.escape-word-puzzle` (but still inside the page) used to never reach
  // this handler, silently discarding even a correct selection. Registered
  // on `document` (see the effect below) so a release ANYWHERE finishes
  // the drag, matching how most drag/selection UIs behave. Stable identity
  // (reads only refs/setState) so it can be registered exactly once.
  const finishSelection = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;

    const cells = selectedCellsRef.current;
    selectedCellsRef.current = [];
    setSelectedCells([]);
    if (cells.length === 0) return;

    const word = cells.map((c) => grid[c.row][c.col]).join('');
    const reversed = word.split('').reverse().join('');
    const isPositive = POSITIVE_WORDS.includes(word) || POSITIVE_WORDS.includes(reversed);
    const canonical = POSITIVE_WORDS.includes(word) ? word : reversed;

    if (isPositive && !foundWordsRef.current.has(canonical)) {
      foundWordsRef.current.add(canonical);
      setFoundCellKeys((prev) => {
        const next = new Set(prev);
        cells.forEach((c) => next.add(key(c.row, c.col)));
        return next;
      });
      setFoundWords((prev) => [...prev, canonical]);
    }
    // Invalid or already-found selections just clear (no persistent state), matching PHP.
  }, [grid]);

  useEffect(() => {
    document.addEventListener('mouseup', finishSelection);
    return () => document.removeEventListener('mouseup', finishSelection);
  }, [finishSelection]);

  const cellClass = (row, col) => {
    const k = key(row, col);
    if (foundCellKeys.has(k)) return 'escape-grid-cell escape-grid-cell--found';
    if (selectedKeys.has(k)) return 'escape-grid-cell escape-grid-cell--highlighted';
    return 'escape-grid-cell';
  };

  const handleTouchMove = (event) => {
    if (!draggingRef.current) return;
    event.preventDefault();
    const touch = event.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const row = el?.dataset?.row;
    const col = el?.dataset?.col;
    if (row !== undefined && col !== undefined) {
      extendSelection(Number(row), Number(col));
    }
  };

  const handleSubmit = () => {
    if (submitted || foundWords.length < 1) return;
    setSubmitted(true);
    setTimeout(() => {
      onComplete(foundWords.length);
    }, 1000);
  };

  return (
    <ActivityPopup
      open={open}
      title={CHEST_TITLE}
      subtitle={CHEST_SUBTITLE}
      plainHeader
      boldTitle={false}
      testId="escape-activity-chest"
      footer={
        foundWords.length >= 1 ? (
          <button
            type="button"
            className="escape-submit-btn"
            disabled={submitted}
            aria-label="Submit"
            data-testid="escape-chest-submit"
            onClick={handleSubmit}
          />
        ) : null
      }
    >
      <div className="escape-chest-layout">
        <div className="escape-word-puzzle" onTouchEnd={finishSelection}>
          <table id="puzzle-grid" data-testid="escape-puzzle-grid">
            <tbody>
              {grid.map((rowLetters, row) => (
                <tr key={row}>
                  {rowLetters.map((letter, col) => (
                    <td
                      key={col}
                      className={cellClass(row, col)}
                      data-row={row}
                      data-col={col}
                      data-testid={`escape-puzzle-cell-${row}-${col}`}
                      onMouseDown={() => beginSelection(row, col)}
                      onMouseEnter={() => extendSelection(row, col)}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        beginSelection(row, col);
                      }}
                      onTouchMove={handleTouchMove}
                    >
                      {letter}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="escape-chest-words">
          {/* room.php's .matchtitle-text never changes text — it's a
              static label, not a live "words found" counter. */}
          <h3 className="escape-chest-words-title">Words will appear here</h3>
          <div className="escape-chest-words-list" data-testid="escape-chest-found-words">
            {foundWords.map((word) => (
              <input key={word} type="text" readOnly value={word} data-testid={`escape-chest-word-${word}`} />
            ))}
          </div>
        </div>
      </div>
    </ActivityPopup>
  );
}

export default ActivityFiveWordPuzzle;
